import { CSSProperties } from '@material-ui/core/styles/withStyles';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { CircularProgress } from '@material-ui/core';
import { capitalizeAll, GenericMessageBox } from '@moderepo/mode-app-components';
import { modeUIContext, setIsEditingForm } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import { fetchAllDeviceKVStores, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { useSelectAllDeviceKVStores, useSelectDeviceConfigSchemas, useSelectProject } from '../../utils/dataStateHooks';
import { DeviceConfigComp, DeviceConfigCompProps } from '../../components/devices';
import { useRitzTranslation } from '../../utils/customHooks';
import { clearDeviceConfigInputErrorForParamName, createDeviceConfigInputs, createUpdateDeviceConfigDDPActions, deleteUnusedDDPs,
    validateDeviceConfigInputs } from './utils';
import { DeviceConfigInputErrors, DeviceConfigInputs } from '../../components/devices/utils';


const deviceConfigComp = {};



export interface DeviceConfigContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;

    readonly projectId: number;

    // All these props are required for identifying the selected device config schema
    readonly deviceId: number;
    readonly firmwareComponentName: string;
    readonly firmwareComponentVersionName: string;

    // Only available if the agent is looking at a user's device
    readonly userId?: number | undefined;
    readonly homeId?: number | number;
}


/**
 * This is the wrapper for the DeviceConfigComp. This contains the logic for fetching DCS AND DDP that are created by the schema
 * and passing them to the DeviceConfigComp for rendering. This container also contain the logic for saving the config to DDP.
 */
export const DeviceConfigContainer: React.FC<DeviceConfigContainerProps> = (props: DeviceConfigContainerProps) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { dispatch:appDataDispatch } = useContext(userAppDataContext);

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [inputErrors, setInputErrors] = useState<DeviceConfigInputErrors>([]);

    // Fetch all schemas
    const deviceConfigSchemas = useSelectDeviceConfigSchemas(props.deviceId, false, true, true);

    // Once we have all the schemas, find the schema that has the same componentName and componentVersionName as the one in props
    const selectedSchema = useMemo(() => {
        return deviceConfigSchemas?.find((schema) => {
            return schema.componentName === props.firmwareComponentName && schema.componentVersionName === props.firmwareComponentVersionName;
        });
    }, [deviceConfigSchemas, props.firmwareComponentName, props.firmwareComponentVersionName]);


    // Fetch all device KV
    const allDDPs = useSelectAllDeviceKVStores(props.deviceId);

    // Once we have the schema and DDP, we can create device config inputs object using the schema and ddp values
    const deviceConfigInputs = useMemo((): DeviceConfigInputs => {
        if (selectedSchema && selectedSchema.deviceConfigSchema && allDDPs) {
            return createDeviceConfigInputs(props.deviceId, selectedSchema.deviceConfigSchema, allDDPs);
        }
        return [];
    }, [allDDPs, props.deviceId, selectedSchema]);



    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputErrors([]);

        onChangeEditingMode(false);
    }, [onChangeEditingMode]);



    /**
     * On user form input change, we will clear the error message for that specific input.
     */
    const onDeviceConfigFormInputChange: DeviceConfigCompProps['onFormInputChange'] = useCallback((
        paramKey: string, value: any, paramSetIndex: number, paramSetInstanceIndex: number,
    ) => {
        setInputErrors((currentInputErrors) => {
            return clearDeviceConfigInputErrorForParamName(currentInputErrors, paramKey, paramSetIndex, paramSetInstanceIndex);
        });
    }, []);



    /**
     * On form submit, validate the inputs and then write the inputs to DDP if inputs are valid
     */
    const onDeviceConfigFormSubmit = useCallback(async (newDeviceConfigInputs: DeviceConfigInputs) => {

        const newInputErrors = validateDeviceConfigInputs(newDeviceConfigInputs, selectedSchema?.deviceConfigSchema, true, trans);

        // Flatten the error objects into 1-D array of error input objects
        const hasErrors = newInputErrors.flat().find((paramSetInstanceInputsErrors) => {
            // go through each error object for 1 paramSet instance inputs and return true if there is at least 1 error message in the object
            return Object.values(paramSetInstanceInputsErrors).find((errorMessage) => {
                return errorMessage !== undefined;
            });
        });

        if (!hasErrors && selectedSchema?.deviceConfigSchema !== undefined) {
            const deviceConfigSchema = selectedSchema.deviceConfigSchema;
            const ddpKeysToBeDeleted: string[] = [];

            // Because we need to save the config in multiple DDPs, we will create and batch all the actions needed to write the config to DDP
            // and then call them in 1 promise
            const updateDeviceKVActions: Promise<void>[] = [];

            createUpdateDeviceConfigDDPActions(
                props.deviceId,
                deviceConfigSchema,
                newDeviceConfigInputs,
                deviceConfigInputs,
                allDDPs,
                updateDeviceKVActions,
                ddpKeysToBeDeleted,
                appDataDispatch,
            );

            if (updateDeviceKVActions.length > 0) {
                // Dispatch all actions and wait for response
                const success = await Promise.allSettled(updateDeviceKVActions).then((results) => {
                    const hasErrors = results.some((oneResult) => {
                        return oneResult.status === 'rejected';
                    });
                    return !hasErrors;
                });

                if (!success) {
                    return;
                }

                // Dispatch an action to refetch all deviceKVStore
                await appDataDispatch(fetchAllDeviceKVStores(props.deviceId));
            }

            // If there are DDP Keys to be deleted, call "deleteUnusedDDPs" to delete those DDPs
            if (ddpKeysToBeDeleted.length > 0) {
                await deleteUnusedDDPs(props.deviceId, ddpKeysToBeDeleted, trans, uiDispatch, appDataDispatch);
            }

            closeEditing();
        }

        setInputErrors(newInputErrors);

    }, [selectedSchema?.deviceConfigSchema, trans, props.deviceId, deviceConfigInputs, allDDPs, appDataDispatch, closeEditing, uiDispatch]);



    /**
     * Create the set of actions available for the DeviceConfigComp and cache it
     */
    const deviceConfigFormActions = useMemo((): DeviceConfigCompProps['actions'] => {
        if (!selectedSchema || !selectedSchema.deviceConfigSchema || selectedSchema.deviceConfigSchema.length === 0) {
            return undefined;
        }

        return {
            startEdit: {
                onClick: () => {
                    onChangeEditingMode(true);
                },
            },
            cancelEdit: {
                label  : capitalizeAll(trans('label.cancel')),
                onClick: closeEditing,
            },
            saveUpdate: {
                label  : capitalizeAll(trans('label.save')),
                onClick: onDeviceConfigFormSubmit,
            },
        };
    }, [closeEditing, onChangeEditingMode, onDeviceConfigFormSubmit, selectedSchema, trans]);


    /**
     * Create the texts for the DeviceConfigComp and cache it
     */
    const deviceConfigFormTexts = useMemo((): DeviceConfigCompProps['texts'] => {
        return {
            emptyRepeatableParamSetInputsMessage: trans('device_config.empty_repeatable_param_set_inputs.message'),
            add                                 : trans('label.add'),
        };
    }, [trans]);


    return (
        <>
            {selectedSchema && allDDPs && deviceConfigInputs && (
                <DeviceConfigComp
                    className={clsx(props.className, isEditing && 'editing')}
                    style={props.style}
                    title={capitalizeAll(trans('label.device_config'))}
                    icon="settings"
                    isEditing={isEditing}
                    deviceId={props.deviceId}
                    deviceConfigSchema={selectedSchema.deviceConfigSchema}
                    deviceConfigInputs={deviceConfigInputs}
                    inputErrors={inputErrors}
                    fields={deviceConfigComp}
                    texts={deviceConfigFormTexts}
                    onFieldClicked={() => {
                        onChangeEditingMode(true);
                    }}
                    onFormInputChange={onDeviceConfigFormInputChange}
                    actions={deviceConfigFormActions}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.device_config_schema', { count: 2 })),
                            })}
                        />
                    )}
                />
            )}

            {!selectedSchema && (
                <CircularProgress />
            )}
        </>
    );
};
